import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  unref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue';

interface UseRotatingImagesOptions {
  imageSrcs: string[] | Ref<string[]> | ComputedRef<string[]>;
  backgroundImageSrc?: string;
  rotationIntervalMs?: number;
  fadeDurationMs?: number;
}

const DEFAULT_ROTATION_INTERVAL_MS = 5000;
const DEFAULT_FADE_DURATION_MS = 200;

const registerLifecycleHooks = (startRotation: () => void, stopRotation: () => void) => {
  onMounted(startRotation);
  onBeforeUnmount(stopRotation);
};

export const useRotatingImages = (options: UseRotatingImagesOptions) => {
  const {
    imageSrcs,
    backgroundImageSrc,
    rotationIntervalMs = DEFAULT_ROTATION_INTERVAL_MS,
    fadeDurationMs = DEFAULT_FADE_DURATION_MS,
  } = options;

  const currentImageIndex = ref(0);
  const overlayImageSrc = ref('');

  let rotationInterval: ReturnType<typeof setInterval> | undefined;
  let overlayTimeout: ReturnType<typeof setTimeout> | undefined;
  const resolvedImageSrcs = computed(() => unref(imageSrcs));

  const preloadImage = (imageUrl: string): Promise<void> =>
    new Promise((resolve) => {
      if (typeof Image === 'undefined') {
        resolve();
        return;
      }

      const image = new Image();
      image.onload = () => resolve();
      image.onerror = () => resolve();
      image.src = imageUrl;
    });

  const preloadImages = (urls: string[]) => Promise.all(urls.map((url) => preloadImage(url)));

  const currentImageSrc = computed(() => resolvedImageSrcs.value[currentImageIndex.value] ?? '');

  const clearOverlayTimeout = () => {
    if (overlayTimeout) {
      clearTimeout(overlayTimeout);
      overlayTimeout = undefined;
    }
  };

  const transitionToNextImage = () => {
    const currentImageSrcs = resolvedImageSrcs.value;
    if (!currentImageSrcs.length) {
      return;
    }

    overlayImageSrc.value = currentImageSrc.value;
    currentImageIndex.value = (currentImageIndex.value + 1) % currentImageSrcs.length;

    clearOverlayTimeout();
    overlayTimeout = setTimeout(() => {
      overlayImageSrc.value = '';
    }, fadeDurationMs);
  };

  const startRotation = () => {
    if (rotationInterval) {
      clearInterval(rotationInterval);
    }

    if (resolvedImageSrcs.value.length <= 1) {
      rotationInterval = undefined;
      return;
    }

    rotationInterval = setInterval(transitionToNextImage, rotationIntervalMs);
  };

  const stopRotation = () => {
    if (rotationInterval) {
      clearInterval(rotationInterval);
      rotationInterval = undefined;
    }
    clearOverlayTimeout();
  };

  watch(resolvedImageSrcs, (nextImageSrcs, previousImageSrcs) => {
    const previousImageSrc = previousImageSrcs?.[currentImageIndex.value];
    const nextImageIndex = previousImageSrc ? nextImageSrcs.indexOf(previousImageSrc) : -1;

    currentImageIndex.value = nextImageIndex >= 0 ? nextImageIndex : 0;
    overlayImageSrc.value = '';
    preloadImages(nextImageSrcs);
    startRotation();
  });

  preloadImages(resolvedImageSrcs.value);
  if (backgroundImageSrc) {
    preloadImage(backgroundImageSrc);
  }

  registerLifecycleHooks(startRotation, stopRotation);

  return {
    currentImageSrc,
    overlayImageSrc,
    backgroundImageSrc: backgroundImageSrc ?? '',
  };
};
